import is from '@sindresorhus/is';

import { COMMENT_ID_ATTRIBUTE } from '~/ui/core/rich-editor/CommentMarkNode';

import type { CommentTarget } from '../types';

export { COMMENT_ID_ATTRIBUTE };

export const ANCHOR_ROOT_ATTRIBUTE = 'data-anchor-root';
export const ANCHOR_KIND_ATTRIBUTE = 'data-anchor-kind';
export const ANCHOR_ITEM_ID_ATTRIBUTE = 'data-item-id';
export const ANCHOR_SKILL_ID_ATTRIBUTE = 'data-skill-id';
export const ANCHOR_WORKFLOW_STEP_ID_ATTRIBUTE = 'data-workflow-step-id';
export const ANCHOR_STEP_INDEX_ATTRIBUTE = 'data-step-index';
export const COMMENT_BUTTON_ATTRIBUTE = 'data-comment-button';
export const COMMENT_COMPOSER_ATTRIBUTE = 'data-comment-composer';
export const COMMENT_RAIL_ATTRIBUTE = 'data-comment-rail';

const ANCHOR_KIND = {
    title: 'title',
    skill: 'skill',
    descriptionSection: 'description-section',
    workflowStep: 'workflow-step',
    description: 'description',
    workflowStepTitle: 'workflow-step-title',
    workflowStepDescription: 'workflow-step-description',
} as const satisfies Record<string, CommentTarget['kind']>;

export type WorkflowStepPartKind = typeof ANCHOR_KIND.workflowStepDescription | typeof ANCHOR_KIND.workflowStepTitle;

const anchorKindSelector = (kind: CommentTarget['kind']): string => `[${ANCHOR_KIND_ATTRIBUTE}="${kind}"]`;

export const TITLE_ANCHOR_SELECTOR = anchorKindSelector(ANCHOR_KIND.title);
export const SKILL_ANCHOR_SELECTOR = anchorKindSelector(ANCHOR_KIND.skill);
export const DESCRIPTION_SECTION_ANCHOR_SELECTOR = anchorKindSelector(ANCHOR_KIND.descriptionSection);
export const WORKFLOW_STEP_ANCHOR_SELECTOR = anchorKindSelector(ANCHOR_KIND.workflowStep);

export const DESCRIPTION_ANCHOR_SELECTOR = anchorKindSelector(ANCHOR_KIND.description);

// Both comment vocabularies mark a commentable description body; selection and outside-click
// handling must treat either as the same surface.
export const ANY_DESCRIPTION_ANCHOR_SELECTOR = `${DESCRIPTION_SECTION_ANCHOR_SELECTOR}, ${DESCRIPTION_ANCHOR_SELECTOR}`;

export const workflowStepPartAnchorSelectorAny = `${anchorKindSelector(ANCHOR_KIND.workflowStepTitle)}, ${anchorKindSelector(ANCHOR_KIND.workflowStepDescription)}`;

export const workflowStepPartAnchorSelector = (kind: WorkflowStepPartKind, stepIndex: number): string =>
    `${anchorKindSelector(kind)}[${ANCHOR_STEP_INDEX_ATTRIBUTE}="${stepIndex}"]`;

export const anchorRootSelector = (itemId: string): string => `[${ANCHOR_ROOT_ATTRIBUTE}="${itemId}"]`;

export const titleAnchorSelector = (itemId: string): string =>
    `${TITLE_ANCHOR_SELECTOR}[${ANCHOR_ITEM_ID_ATTRIBUTE}="${itemId}"]`;

export const skillAnchorSelector = (skillId: string): string =>
    `${SKILL_ANCHOR_SELECTOR}[${ANCHOR_SKILL_ID_ATTRIBUTE}="${skillId}"]`;

export const workflowStepAnchorSelector = (workflowStepId: string): string =>
    `${WORKFLOW_STEP_ANCHOR_SELECTOR}[${ANCHOR_WORKFLOW_STEP_ID_ATTRIBUTE}="${workflowStepId}"]`;

export const commentMarkSelector = (commentId?: string): string =>
    is.undefined(commentId) ? `mark[${COMMENT_ID_ATTRIBUTE}]` : `mark[${COMMENT_ID_ATTRIBUTE}="${commentId}"]`;

export const anchorRootAttrs = (itemId: string): Record<string, string> => ({
    [ANCHOR_ROOT_ATTRIBUTE]: itemId,
});

export const titleAnchorAttrs = ({
    itemId,
    commentId,
}: {
    readonly itemId: string;
    readonly commentId?: string;
}): Record<string, string> => ({
    [ANCHOR_KIND_ATTRIBUTE]: ANCHOR_KIND.title,
    [ANCHOR_ITEM_ID_ATTRIBUTE]: itemId,
    ...(is.undefined(commentId) ? {} : { [COMMENT_ID_ATTRIBUTE]: commentId }),
});

export const skillAnchorAttrs = ({
    itemId,
    skillId,
    commentId,
}: {
    readonly itemId: string;
    readonly skillId: string;
    readonly commentId?: string;
}): Record<string, string> => ({
    [ANCHOR_KIND_ATTRIBUTE]: ANCHOR_KIND.skill,
    [ANCHOR_SKILL_ID_ATTRIBUTE]: skillId,
    [ANCHOR_ITEM_ID_ATTRIBUTE]: itemId,
    ...(is.undefined(commentId) ? {} : { [COMMENT_ID_ATTRIBUTE]: commentId }),
});

/** Both comment vocabularies mark the body they anchor to; only the kind differs. */
export const descriptionAnchorAttrsFor = (
    kind: typeof ANCHOR_KIND.description | typeof ANCHOR_KIND.descriptionSection,
    itemId: string,
): Record<string, string> => ({
    [ANCHOR_KIND_ATTRIBUTE]: kind,
    [ANCHOR_ITEM_ID_ATTRIBUTE]: itemId,
});

export const descriptionSectionAnchorAttrs = ({ itemId }: { readonly itemId: string }): Record<string, string> =>
    descriptionAnchorAttrsFor(ANCHOR_KIND.descriptionSection, itemId);

export const workflowStepPartAnchorAttrs = ({
    itemId,
    kind,
    stepIndex,
    commentId,
}: {
    readonly itemId: string;
    readonly kind: WorkflowStepPartKind;
    readonly stepIndex: number;
    readonly commentId?: string;
}): Record<string, string> => ({
    [ANCHOR_KIND_ATTRIBUTE]: kind,
    [ANCHOR_ITEM_ID_ATTRIBUTE]: itemId,
    [ANCHOR_STEP_INDEX_ATTRIBUTE]: String(stepIndex),
    ...(is.undefined(commentId) ? {} : { [COMMENT_ID_ATTRIBUTE]: commentId }),
});
